import React, { useState, useEffect } from 'react';
import { database } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import './SmsMessages.css';

const SmsMessages = ({ selectedDevice }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedDevice || !selectedDevice.id) {
      setMessages([]);
      return;
    }

    setLoading(true);
    
    // Listen to SMS messages for the selected device from user_sms_messages
    const messagesRef = ref(database, `user_sms_messages/${selectedDevice.id}`);
    
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const messagesList = Object.keys(data).map(messageId => ({
          id: messageId,
          ...data[messageId]
        }));
        
        // Sort messages by timestamp (newest first)
        messagesList.sort((a, b) => {
          const timeA = a.timestamp || a.captured_at || 0;
          const timeB = b.timestamp || b.captured_at || 0;
          return new Date(timeB) - new Date(timeA);
        });
        
        setMessages(messagesList);
      } else {
        setMessages([]);
      }
      setLoading(false);
    });

    // Cleanup listener
    return () => off(messagesRef, 'value', unsubscribe);
  }, [selectedDevice]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const isNewMessage = (timestamp) => {
    if (!timestamp) return false;
    const messageTime = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = (now - messageTime) / (1000 * 60);
    return diffInMinutes < 5; // Messages within 5 minutes are considered new
  };

  if (!selectedDevice) {
    return (
      <div className="sms-messages">
        <div className="no-device-selected">
          <h3>📱 Select a device to view SMS messages</h3>
          <p>Click on a device from the list to see all SMS messages</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sms-messages">
      <div className="messages-header">
        <h3>📨 SMS Messages - {selectedDevice.deviceName || selectedDevice.name || 'Unknown Device'}</h3>
        <p>Mobile: {selectedDevice.mobileNumber || selectedDevice.phoneNumber || 'Not available'}</p>
        {messages.length > 0 && (
          <p className="message-count">Total Messages: {messages.length}</p>
        )}
      </div>

      {loading ? (
        <div className="loading">Loading messages...</div>
      ) : messages.length === 0 ? (
        <div className="no-messages">
          <p>No SMS messages found for this device</p>
        </div>
      ) : (
        <div className="messages-list">
          {messages.map((message) => (
            <div 
              key={message.id} 
              className={`message-card ${isNewMessage(message.timestamp || message.date) ? 'new-message' : ''}`}
            >
              {isNewMessage(message.timestamp || message.date) && (
                <div className="new-badge">NEW</div>
              )}
              
              <div className="message-header">
                <div className="sender-info">
                  <strong>From: {message.sender || message.from || 'Unknown'}</strong>
                </div>
                <div className="timestamp">
                  {formatTimestamp(message.timestamp || message.date)}
                </div>
              </div>
              
              <div className="message-body">
                <p>{message.body || message.text || message.message || 'No content'}</p>
              </div>
              
              {message.type && (
                <div className="message-type">
                  <span className="type-badge">Type: {message.type}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SmsMessages;
